import React, { PureComponent } from "react";
import reactElementToJSXString from "react-element-to-jsx-string";
import { Table, Input, Button, Icon, Dropdown } from "semantic-ui-react";
import { sortBy } from "lodash";
import LoadingPlaceholder from "./LoadingPlaceholder";
import { renderToStaticMarkup } from "react-dom/server";

class DataTable extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            headers: this.props.headers,
            data: this.props.data,
            limit: this.props.limit ?? 25,
            sort: {
                column: this.props.sort ?? "createdAt",
                order: this.props.order ?? "asc",
            },
            search: this.props.searchTerm || "",
            quickSort: {
                column: null,
                direction: null,
            },
            dataCount: 0,
        };

        this.searchTimer = null;

        this.limitDropdownOptions = [
            { key: 0, text: "5 per page", value: 5 },
            { key: 1, text: "10 per page", value: 10 },
            { key: 2, text: "25 per page", value: 25 },
            { key: 3, text: "50 per page", value: 50 },
            { key: 4, text: "100 per page", value: 100 },
            { key: 5, text: "250 per page", value: 250 },
            { key: 6, text: "500 per page", value: 500 },
            { key: 7, text: "750 per page", value: 750 },
            { key: 8, text: "1000 per page", value: 1000 },
            { key: 9, text: "View all records", value: 100000 },
        ];
    }

    displayHeaders() {
        const { quickSort } = this.state;
        const { headers } = this.props;
        return headers.map((header, index) => {
            return (
                <Table.HeaderCell
                    sorted={
                        header.key === quickSort.column
                            ? quickSort.direction
                            : null
                    }
                    onClick={() =>
                        this.props.quickSort === false
                            ? null
                            : this.onChangeQuickSort(header.key)
                    }
                    width={header.width}
                    {...(header.collapsing ? { collapsing: true } : {})}
                    key={index}
                >
                    {header.text}
                </Table.HeaderCell>
            );
        });
    }

    getChangeState() {
        return {
            page: this.props.page,
            limit: this.state.limit,
            sort: this.state.sort.column,
            order: this.state.sort.order,
            term: this.state.search,
        };
    }

    displayBody() {
        const { quickSort } = this.state;
        const { headers } = this.props;
        let data = this.getSearchData();
        if (quickSort.column !== null) {
            data = this.sortData(data);
        }

        return data.map((field, index) => {
            return (
                <Table.Row key={index}>
                    {headers.map((header, i) => (
                        <Table.Cell
                            key={i}
                            {...(header.collapsing ? { collapsing: true } : {})}
                            style={{ whiteSpace: "pre-line" }}
                        >
                            {field[header.key].value}
                        </Table.Cell>
                    ))}
                </Table.Row>
            );
        });
    }

    sortData(data) {
        const { quickSort } = this.state;
        let sorted = sortBy(
            data,
            (properties) => properties[quickSort.column].search,
        );
        if (quickSort.direction === "descending") {
            return sorted.reverse();
        }

        return sorted;
    }

    onChangeSearch(value) {
        this.setState({ search: value });
        // If a callback for state changes was provided, call it
        if (typeof this.props.onChange === "function") {
            const changeState = this.getChangeState();
            changeState.term = value;
            changeState.page = 1;
            if (value.length === 0) {
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
                return this.props.onChange(changeState);
            }

            if (this.searchTimer !== null) {
                clearTimeout(this.searchTimer);
            }

            this.searchTimer = setTimeout(() => {
                this.props.onChange(changeState);
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
            }, 1000);
            return;
        }
    }

    onChangePerPageLimit(event, selection) {
        const limit = selection.value;
        const changeState = this.getChangeState();
        changeState.limit = limit;
        this.setState({ limit });
        if (typeof this.props.onChange === "function") {
            this.props.onChange(changeState);
        }
    }

    onChangeSortColumn(event, selection) {
        const { sort } = this.state;
        const column = selection.value;
        const changeState = this.getChangeState();
        changeState.sort = column;
        this.setState({ sort: { order: sort.order, column } });
        if (typeof this.props.onChange === "function") {
            this.props.onChange(changeState);
        }
    }

    onChangeSortOrder(event, selection) {
        const { sort } = this.state;
        sort.order = sort.order === "asc" ? "desc" : "asc";
        const changeState = this.getChangeState();
        changeState.order = sort.order;
        this.setState({ sort });
        if (typeof this.props.onChange === "function") {
            this.props.onChange(changeState);
        }
    }

    onChangeQuickSort(selectedColumn) {
        const { quickSort } = this.state;

        if (quickSort.column === selectedColumn) {
            return this.setState({
                quickSort: {
                    direction:
                        quickSort.direction === "ascending"
                            ? "descending"
                            : "ascending",
                    column: quickSort.column,
                },
            });
        }

        this.setState({
            quickSort: {
                direction: "ascending",
                column: selectedColumn,
            },
        });
    }

    getSearchData() {
        // If a callback was provided for searching, we are using server-side searching
        if (typeof this.props.onChange === "function") {
            return this.props.data;
        }

        // Otherwise, perform the search locally
        if (this.state.search.length === 0) {
            return this.props.data;
        }

        return this.props.data.filter((row) => {
            for (let key in row) {
                const prop = row[key];
                if (typeof prop.search !== "undefined") {
                    if (
                        String(prop.search)
                            .toLowerCase()
                            .includes(String(this.state.search).toLowerCase())
                    ) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    resultsCount() {
        if (this.props.hideResultsCount === true) {
            return;
        }

        const { dataCount } = this.props;
        let countText = "No records found";
        const totalRecords = this.props.totalRecords || this.props.data.length;
        if (this.props.data.length > 0) {
            countText =
                "Showing " +
                this.getSearchData().length +
                " of " +
                totalRecords +
                " records";
        }

        return (
            <>
                <p>{countText}</p>
                <p>{dataCount}</p>
            </>
        );
    }

    disableNext() {
        return this.props.page >= this.props.totalRecords / this.state.limit;
    }

    onClickNext() {
        const changeState = this.getChangeState();
        changeState.page = this.props.page + 1;
        return this.props.onChange(changeState);
    }

    disablePrevious() {
        return this.props.page === 1;
    }

    onClickPrevious() {
        const changeState = this.getChangeState();
        changeState.page = this.props.page - 1;
        return this.props.onChange(changeState);
        // this.props.previous(this.props.page - 1);
    }

    pagination() {
        if (this.props.pagination === true) {
            return (
                <div className='text-right'>
                    <Button
                        size='tiny'
                        disabled={this.disablePrevious()}
                        onClick={this.onClickPrevious.bind(this)}
                    >
                        Previous
                    </Button>
                    <span className='text-center ml-3 mr-3'>
                        Page {this.props.page}
                    </span>
                    <Button
                        size='tiny'
                        disabled={this.disableNext()}
                        onClick={this.onClickNext.bind(this)}
                    >
                        Next
                    </Button>
                </div>
            );
        }

        return null;
    }

    exportCSV() {
        // Place these helpers near exportCSV()
        const pickFirst = (...candidates) =>
            candidates.find((v) => {
                const s = typeof v === "number" ? String(v) : v;
                return typeof s === "string" && s.trim().length;
            }) || "-";

        const memberToText = (m = {}) => {
            // Adjust ordering to your data shape
            const full = [m.firstName, m.lastName]
                .filter(Boolean)
                .join(" ")
                .trim();
            return pickFirst(
                m.displayName,
                m.name,
                full,
                m.fullName,
                m.username,
                m.email,
                m.code,
                m.id,
                m._id,
            );
        };

        // Optional: choose whether to include "detail" text and/or href in CSV
        const EXPORT_OPTIONS = {
            includeDetail: true, // set false if you only want "test test"
            includeHref: false, // set true to append the link
        };

        // Extracts "test test" from the <a>, plus optional detail/href
        const extractFromAnchorHTML = (html) => {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            const a = tmp.querySelector("a.ui.image.label");
            if (!a) {
                // Fallback: plain text content
                const t = (tmp.textContent || tmp.innerText || "")
                    .replace(/\s+/g, " ")
                    .trim();
                return t || "-";
            }

            // 1) main label = direct text nodes of <a>, excluding children like <div.detail>
            const main = Array.from(a.childNodes)
                .filter((n) => n.nodeType === Node.TEXT_NODE)
                .map((n) => String(n.nodeValue || "").trim())
                .filter(Boolean)
                .join(" ")
                .trim();

            let parts = [main || "-"];

            // 2) detail text (e.g., "Links")
            if (EXPORT_OPTIONS.includeDetail) {
                const detail = a.querySelector(".detail");
                if (detail) {
                    const dt = detail.textContent?.replace(/\s+/g, " ").trim();
                    if (dt) parts.push(dt);
                }
            }

            // 3) href
            if (EXPORT_OPTIONS.includeHref && a.getAttribute("href")) {
                parts.push(a.getAttribute("href"));
            }

            return parts.filter(Boolean).join(" | ");
        };

        // BOM for Excel UTF-8
        let content = "\ufeff";

        const headers = this.props.headers || [];
        const rows = this.props.data || [];

        // Helper: convert any cell value to plain text
        const cellToText = (val) => {
            if (Array.isArray(val)) {
                console.log("valarr", val);
                const parts = val
                    .map((item) => cellToText(item))
                    .map((s) => String(s || "").trim())
                    .filter((s) => s.length && s !== "-");

                return parts.length ? parts.join(" | ") : "-";
            }

            if (React.isValidElement(val)) {
                // (i) If children is plain text/number, that's easy
                const ch = val.props?.children;
                if (typeof ch === "string" || typeof ch === "number") {
                    return String(ch);
                }

                console.log("val.props", val.props);
                if (val.props?.member) {
                    return memberToText(val.props.member);
                }

                // (iii) Try to SSR -> parse anchor structure
                try {
                    const html = renderToStaticMarkup(val);
                    const text = extractFromAnchorHTML(html);
                    if (text && text !== "-") return text;
                } catch (e) {
                    // noop – fall through
                }

                // (iv) Last resort: JSX → strip tags
                try {
                    return (
                        reactElementToJSXString(val)
                            .replace(/<[^>]*>/g, " ")
                            .replace(/\s+/g, " ")
                            .trim() || "-"
                    );
                } catch {
                    return "-";
                }
            }

            console.log("valother", val);
            // Plain objects: stringify (last resort)
            if (typeof val === "object") {
                try {
                    return JSON.stringify(val);
                } catch {
                    return "-";
                }
            }

            return String(val);
        };

        // Helper: CSV escape (wrap in quotes, double internal quotes)
        const csv = (s) => `"${String(s).replace(/"/g, '""')}"`;

        // Header row (in declared order)
        content +=
            headers.map((h) => csv(h.text ?? h.key ?? "")).join(",") + "\r\n";

        // Data rows (also in declared header order)
        for (const row of rows) {
            const cols = headers.map((h) => {
                const cell = row?.[h.key]?.value; // your structure: { value, search }
                const text = cellToText(cell);
                return csv(text);
            });
            content += cols.join(",") + "\r\n";
        }

        // Filename
        const fileName = (
            new Date().toDateString() +
            "-" +
            document.location.pathname.substring(1)
        )
            .replace(/ /g, "-")
            .replace(/\//g, "-");

        // Download
        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName + ".csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    showPerPage() {
        if (typeof this.props.limit === "number") {
            return (
                <Dropdown
                    selection
                    options={this.limitDropdownOptions}
                    value={this.state.limit}
                    onChange={this.onChangePerPageLimit.bind(this)}
                    className='ml-3'
                />
            );
        }
    }

    showSorting() {
        if (typeof this.props.sortOptions !== "undefined") {
            const sortingDropdownOptions = this.props.sortOptions.map(
                (option, index) => {
                    return { key: index, text: option.text, value: option.key };
                },
            );

            const { sort } = this.state;
            const sortOrderIcon =
                sort.order === "asc" ? "sort amount down" : "sort amount up";
            return (
                <>
                    <Dropdown
                        selection
                        placeholder='Sort by'
                        className='ml-3'
                        value={sort.column}
                        options={sortingDropdownOptions}
                        onChange={this.onChangeSortColumn.bind(this)}
                    />
                    <Button
                        className='ml-1'
                        icon
                        onClick={this.onChangeSortOrder.bind(this)}
                    >
                        <Icon name={sortOrderIcon} />
                    </Button>
                </>
            );
        }
    }

    render() {
        return (
            <div className='table-container'>
                {(this.props.hideExport !== true ||
                    this.props.hideSearch !== true) && (
                    <div className='table-header'>
                        <div className='row'>
                            {this.props.hideExport !== true && (
                                <div className='col-8 d-flex align-items-center'>
                                    <Button
                                        className='m-0'
                                        onClick={this.exportCSV.bind(this)}
                                    >
                                        Export
                                    </Button>
                                    {this.showPerPage()}
                                    {this.showSorting()}
                                </div>
                            )}
                            {this.props.hideSearch !== true && (
                                <div className='col-4 text-right'>
                                    <Input
                                        placeholder='Search'
                                        icon='search'
                                        loading={this.searchTimer !== null}
                                        value={this.state.search}
                                        onChange={({ target }) =>
                                            this.onChangeSearch(target.value)
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {this.props.loading === true ? (
                    <LoadingPlaceholder fluid />
                ) : (
                    <>
                        <Table
                            {...(this.props.noSorting
                                ? {}
                                : { sortable: true })}
                            striped
                            celled
                        >
                            <Table.Header>
                                <Table.Row>{this.displayHeaders()}</Table.Row>
                            </Table.Header>
                            <Table.Body>{this.displayBody()}</Table.Body>
                        </Table>
                        {(this.props.hideResultsCount !== true ||
                            this.props.pagination === true) && (
                            <div className='table-footer'>
                                <div className='row'>
                                    <div className='col'>
                                        {this.resultsCount()}
                                    </div>
                                    <div className='col left-right'>
                                        {this.pagination()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }
}

export default DataTable;
